import type { ServerMessage, BoardState } from '@flipboard/shared';
import { WS_RECONNECT_BASE_MS, WS_RECONNECT_MAX_MS } from '@flipboard/shared';
import { config } from './config.js';

export interface WebSocketClientOptions {
  boardId: string;
  onState: (state: BoardState) => void;
  onMessage: (text: string, clear?: boolean) => void;
  onClear: () => void;
  onResize: (rows: number, cols: number) => void;
  onSound: (enabled: boolean) => void;
  onFillScreen: (enabled: boolean) => void;
  onConnectionChange: (connected: boolean) => void;
  onError: (error: string) => void;
}

export class WebSocketClient {
  private ws: WebSocket | null = null;
  private boardId: string;
  private reconnectAttempts = 0;
  private reconnectTimeout: number | null = null;
  private options: WebSocketClientOptions;
  private isDestroyed = false;

  constructor(options: WebSocketClientOptions) {
    this.options = options;
    this.boardId = options.boardId;
  }

  connect(): void {
    if (this.isDestroyed) return;

    // Build WebSocket URL from config (replace http/https with ws/wss)
    const protocol = config.apiUrl.startsWith('https') ? 'wss:' : 'ws:';
    const host = config.apiHost;
    const url = `${protocol}//${host}/api/boards/${this.boardId}/ws`;

    console.log('[WS] Connecting to:', url);

    try {
      this.ws = new WebSocket(url);

      this.ws.onopen = () => {
        console.log('[WS] Connected to board:', this.boardId);
        this.reconnectAttempts = 0;
        this.options.onConnectionChange(true);
      };

      this.ws.onmessage = (event) => {
        try {
          const message: ServerMessage = JSON.parse(event.data);
          this.handleMessage(message);
        } catch (error) {
          console.error('[WS] Failed to parse message:', error);
        }
      };

      this.ws.onclose = (event) => {
        console.log('[WS] Connection closed:', event.code, event.reason);
        this.options.onConnectionChange(false);

        // If the connection was rejected (1006 = abnormal closure during handshake),
        // it might be due to viewer limit. Check before reconnecting.
        if (event.code === 1006 && this.reconnectAttempts === 0) {
          this.checkViewerLimit();
        } else {
          this.scheduleReconnect();
        }
      };

      this.ws.onerror = (error) => {
        console.error('[WS] Error:', error);
      };
    } catch (error) {
      console.error('[WS] Failed to connect:', error);
      this.scheduleReconnect();
    }
  }

  private async checkViewerLimit(): Promise<void> {
    try {
      const apiBase = config.apiUrl;

      // Check current viewer count
      const viewersUrl = `${apiBase}/api/boards/${this.boardId}/viewers`;
      const response = await fetch(viewersUrl);

      if (response.ok) {
        const data = await response.json();
        const currentViewers = data.viewers;

        // Try to get board info to check the limit
        const boardUrl = `${apiBase}/api/boards/${this.boardId}`;
        const boardResponse = await fetch(boardUrl);

        if (boardResponse.ok) {
          const boardData = await boardResponse.json();
          const tier = boardData.board.ownerTier || 'free';
          const maxViewers = tier === 'free' ? 2 : -1; // free = 2, paid = unlimited

          if (maxViewers !== -1 && currentViewers >= maxViewers) {
            // Centered on default 6x22 board: 3 newlines + 1 space before text
            const errorMessage = '\n\n\n VIEWER LIMIT REACHED';
            console.error('[WS] Viewer limit reached');
            this.options.onError(errorMessage);
            // Don't reconnect for viewer limit errors
            return;
          }
        }
      }
    } catch (error) {
      console.warn('[WS] Failed to check viewer limit:', error);
    }

    // If we couldn't determine it was a viewer limit error, try reconnecting
    this.scheduleReconnect();
  }

  private handleMessage(message: ServerMessage): void {
    switch (message.type) {
      case 'state':
        this.options.onState(message.payload);
        this.cacheState(message.payload);
        break;

      case 'message':
        this.options.onMessage(message.payload.text, message.payload.clear);
        break;

      case 'clear':
        this.options.onClear();
        break;

      case 'resize':
        this.options.onResize(message.payload.rows, message.payload.cols);
        break;

      case 'sound':
        this.options.onSound(message.payload.enabled);
        break;

      case 'fillScreen':
        this.options.onFillScreen(message.payload.enabled);
        break;

      case 'ping':
        this.ws?.send(JSON.stringify({ type: 'pong' }));
        break;

      case 'error':
        console.error(
          '[WS] Server error:',
          message.payload.code,
          message.payload.message
        );
        break;
    }
  }

  private scheduleReconnect(): void {
    if (this.isDestroyed) return;

    const delay = Math.min(
      WS_RECONNECT_BASE_MS * Math.pow(2, this.reconnectAttempts),
      WS_RECONNECT_MAX_MS
    );

    console.log(
      `[WS] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts + 1})`
    );

    this.reconnectTimeout = window.setTimeout(() => {
      this.reconnectAttempts++;
      this.connect();
    }, delay);
  }

  private cacheState(state: BoardState): void {
    try {
      localStorage.setItem(`flipboard:${this.boardId}`, JSON.stringify(state));
    } catch {
      // localStorage may be unavailable
    }
  }

  getCachedState(): BoardState | null {
    try {
      const cached = localStorage.getItem(`flipboard:${this.boardId}`);
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  }

  disconnect(): void {
    this.isDestroyed = true;
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
    }
    if (this.ws) {
      this.ws.close(1000, 'Client disconnect');
      this.ws = null;
    }
  }
}
